/* Delivery. `notifyPrefs.js` decides *what* should be sent and when;
 * `native.js` owns the Capacitor plugin; this is the door the rest of the app
 * knocks on, and the only place that knows there is more than one platform.
 *
 * The three channels are not equal, and the UI says so rather than pretending:
 *
 *   Android / iOS — real local notifications. Queued with the OS, fire whether
 *     or not the app is running. This is the one that actually defends a
 *     streak.
 *   Web — the Notification API, which can only fire while a tab is open. No
 *     service worker, no push server; `window.Notification` in a live page is
 *     the whole mechanism. Genuinely useful if you leave the app open in a
 *     background tab, and useless otherwise — so `deviceSupport()` reports it
 *     as `background: false` and Settings prints the caveat instead of
 *     letting the user assume otherwise.
 *   Email — not a client channel at all; see the recap work. Its preference
 *     lives here so there is one place to switch everything off.
 *
 * Prefs are mirrored to `users/{uid}/app/notifications` so they follow the
 * account (and so the "email me" opt-in is legible to anything server-side
 * that later drains the queue). localStorage is the source of truth for the
 * device you are holding; the mirror is read once at sign-in.
 */
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "./firebase.js";
import { isNative, notificationPermission, requestNotificationPermission, setScheduledNotifications } from "./native.js";
import { loadLearn } from "./learn/persist.js";
import { mergePrefs, normalizePrefs, PRACTICE_IDS, practiceSchedule, RECAP_ID, recapSchedule } from "./notifyPrefs.js";

/* Every id this module owns. Scheduling cancels the lot and re-queues what
 * still applies, so an id that stops being produced also stops firing —
 * which is what makes "turn it off in Settings" take effect immediately
 * rather than a week later. */
const OWNED_IDS = [...PRACTICE_IDS, RECAP_ID];

const KEY = "towardsgm:notify";
export const PREFS_CHANGED = "towardsgm-notify-changed";

/* W2d · the same-day "which opening is due" hint the Learn tab leaves for
 * today's reminder (notifyPrefs.js#practiceMessage). Per device and never
 * synced — it is a fact about today on this device, not account data. */
const DUE_KEY = "towardsgm:notify-due";
export function saveDueHint(hint) {
  try {
    if (hint) localStorage.setItem(DUE_KEY, JSON.stringify(hint));
    else localStorage.removeItem(DUE_KEY);
  } catch {
    /* private mode — the reminder falls back to its generic copy */
  }
}
export function loadDueHint() {
  try {
    return JSON.parse(localStorage.getItem(DUE_KEY)) || null;
  } catch {
    return null;
  }
}

const prefsRef = (uid) => doc(db, "users", uid, "app", "notifications");

let cached = null;
let uid = null;

export function loadNotifyPrefs() {
  if (cached) return cached;
  try {
    cached = normalizePrefs(JSON.parse(localStorage.getItem(KEY)));
  } catch {
    cached = normalizePrefs(null);
  }
  return cached;
}

function persist(next) {
  cached = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode — the prefs just won't survive a reload */
  }
  if (db && uid) setDoc(prefsRef(uid), next, { merge: false }).catch(() => {});
  window.dispatchEvent(new Event(PREFS_CHANGED));
}

/* Patches merge one level deep so a caller can set `{ practice: { at } }`
 * without restating `device`. `updatedAt` is stamped here rather than by
 * callers because it is what decides the cross-device merge — a caller that
 * forgot it would silently lose every future sync. */
export function saveNotifyPrefs(patch) {
  const base = loadNotifyPrefs();
  const next = normalizePrefs({
    ...base,
    ...patch,
    practice: { ...base.practice, ...patch.practice },
    recap: { ...base.recap, ...patch.recap },
    updatedAt: Date.now(),
  });
  persist(next);
  return next;
}

/* Called when auth settles. Pulls the account's copy, merges it newest-wins
 * (notifyPrefs.js#mergePrefs) and writes the result back to both sides, so a
 * second device adopts the choice instead of quietly overwriting it. */
export async function attachNotifyPrefs(nextUid) {
  uid = nextUid || null;
  if (!db || !uid) return loadNotifyPrefs();
  try {
    const snap = await getDoc(prefsRef(uid));
    const merged = mergePrefs(loadNotifyPrefs(), snap.exists() ? snap.data() : null);
    persist(merged);
  } catch {
    /* offline — keep what this device has */
  }
  return loadNotifyPrefs();
}

/* What this platform can honestly promise.
 *
 * `background` is the distinction that matters and the one users assume: can
 * a reminder reach me when the app is closed? Only the native shells can say
 * yes, which is exactly the sentence Settings shows a web user. */
export function deviceSupport() {
  if (isNative) return { available: true, background: true };
  const ok = typeof window !== "undefined" && "Notification" in window && window.isSecureContext !== false;
  return { available: ok, background: false };
}

export function devicePermission() {
  if (isNative) return null; // async; ask notificationPermission()
  if (!deviceSupport().available) return "unsupported";
  return Notification.permission; // "granted" | "denied" | "default"
}

/* One prompt, at the moment the user asks for reminders — never on load.
 * Returns the resulting state so the caller can keep the toggle honest: a
 * denied permission must not leave a switch showing "on", because nothing
 * would ever arrive and the app would look broken rather than blocked. */
export async function requestDevicePermission() {
  if (isNative) return requestNotificationPermission();
  if (!deviceSupport().available) return "unsupported";
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}

/* ---- the web's half-a-channel ----
 *
 * A single in-page timer for the next due reminder. It dies with the tab, and
 * that is the whole limitation: this exists so someone who works with the app
 * open in a background tab still gets nudged, not so the web can claim parity.
 * 25 hours is a safe ceiling — setTimeout overflows past 2^31 ms and fires
 * immediately, which would turn a next-week reminder into an instant one. */
const MAX_WEB_DELAY = 25 * 60 * 60 * 1000;
let webTimer = null;

function armWebTimer(schedule) {
  clearTimeout(webTimer);
  webTimer = null;
  const next = schedule[0];
  if (!next || devicePermission() !== "granted") return;
  const delay = next.at - Date.now();
  if (delay <= 0 || delay > MAX_WEB_DELAY) return;
  webTimer = setTimeout(() => {
    try {
      new Notification(next.title, { body: next.body, icon: "/icon-192.png", tag: `towardsgm-${next.kind}` });
    } catch {
      /* the tab may have lost permission since */
    }
  }, delay);
}

/* Re-queue everything from the current prefs and the current learn state.
 *
 * Safe to call often, and meant to be: app open, tab regains focus, prefs
 * change, session finished. Each run cancels the whole practice series and
 * re-queues what still applies, which is how "I trained today" removes
 * today's reminder without anything having to track that it had been set. */
export async function syncReminders(ls = null, now = Date.now()) {
  const prefs = loadNotifyPrefs();
  const state = ls || loadLearn();
  const schedule = [...practiceSchedule({ prefs, ls: state, now, dueHint: loadDueHint() }), ...recapSchedule({ prefs, ls: state, now })].sort((a, b) => a.at - b.at);
  if (isNative) {
    const permission = await notificationPermission();
    if (permission !== "granted") return [];
    await setScheduledNotifications(OWNED_IDS, schedule);
    return schedule;
  }
  armWebTimer(schedule);
  return schedule;
}

/* Used by the settings toggle and by account deletion: stop everything now,
 * without waiting for the next sync. */
export async function cancelAllReminders() {
  clearTimeout(webTimer);
  webTimer = null;
  if (isNative) await setScheduledNotifications(OWNED_IDS, []);
}
